import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle, Users, ExternalLink, ArrowRight } from 'lucide-react';
import { contactInfo, faqs } from '../data/mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    institucion: '',
    correo: '',
    telefono: '',
    mensaje: '',
    consultationType: 'general'
  });

  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('/emailer.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          nombre: formData.nombre,
          institucion: formData.institucion,
          correo: formData.correo,
          telefono: formData.telefono,
          mensaje: `Tipo de consulta: ${consultationTypes.find(type => type.value === formData.consultationType)?.label || 'General'}\n\n${formData.mensaje}`
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Mensaje enviado correctamente. Te contactaremos pronto.'
        });
        setFormData({
          nombre: '',
          institucion: '',
          correo: '',
          telefono: '',
          mensaje: '',
          consultationType: 'general'
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.message || 'Error al enviar el mensaje. Inténtalo nuevamente.'
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Error de conexión. Verifica tu conexión a internet e inténtalo nuevamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const consultationTypes = [
    { value: 'general', label: 'Consulta general' },
    { value: 'certification', label: 'Proceso de certificación' },
    { value: 'technical', label: 'Soporte técnico' },
    { value: 'institutional', label: 'Alianzas institucionales' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[400px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1553484771-371a605b060b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
            alt="Centro de contacto y comunicación"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50/65 to-white/75"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageCircle className="h-16 w-16 text-orange-600 mx-auto mb-6" />
          <h1 className="text-5xl font-bold mb-6 text-gray-900">
            <span className="text-orange-600">Contacto</span> y Soporte
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Estamos aquí para apoyarte en tu proceso de certificación. 
            Contáctanos para resolver cualquier duda o consulta.
          </p>
        </div>
      </section>      {/* Contact Methods */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Múltiples Canales de Comunicación
            </h2>
            <p className="text-lg text-gray-600">
              Elige el método que más te convenga para ponerte en contacto con nosotros
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Email */}
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <Mail className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Correo Electrónico
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>{contactInfo.emails.primary}</p>
                <p>{contactInfo.emails.secondary}</p>
                <p className="text-xs text-gray-500">Respuesta en 24-48 horas</p>
              </div>
            </div>

            {/* Phone */}
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <Phone className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Teléfono
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>{contactInfo.phones.international}</p>
                <p>{contactInfo.phones.americas}</p>
                <p className="text-xs text-gray-500">Lun-Vie 9:00-18:00 GMT-6</p>
              </div>
            </div>

            {/* Office */}
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <MapPin className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Oficina Principal
              </h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p>{contactInfo.address.office}</p>
                <p>{contactInfo.address.street}</p>
                <p>{contactInfo.address.city}</p>
                <p>{contactInfo.address.country}</p>
              </div>
            </div>

            {/* Hours */}
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <Clock className="h-12 w-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Horarios de Atención
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p>{contactInfo.officeHours.weekdays}</p>
                <p>{contactInfo.officeHours.weekend}</p>
                <p className="text-red-500">{contactInfo.officeHours.closed}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form and FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Envíanos un Mensaje
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Status Message */}
                {submitStatus.message && (
                  <div className={`p-4 rounded-lg ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-100 border border-green-300 text-green-700' 
                      : 'bg-red-100 border border-red-300 text-red-700'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      required
                      value={formData.nombre}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label htmlFor="correo" className="block text-sm font-medium text-gray-700 mb-2">
                      Correo electrónico *
                    </label>
                    <input
                      type="email"
                      id="correo"
                      name="correo"
                      required
                      value={formData.correo}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="institucion" className="block text-sm font-medium text-gray-700 mb-2">
                      Institución
                    </label>
                    <input
                      type="text"
                      id="institucion"
                      name="institucion"
                      value={formData.institucion}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      disabled={isSubmitting}
                      placeholder="Universidad, colegio, empresa..."
                    />
                  </div>

                  <div>
                    <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      disabled={isSubmitting}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="consultationType" className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de consulta
                  </label>
                  <select
                    id="consultationType"
                    name="consultationType"
                    value={formData.consultationType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    disabled={isSubmitting}
                  >
                    {consultationTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="mensaje" className="block text-sm font-medium text-gray-700 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    required
                    rows={4}
                    value={formData.mensaje}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    placeholder="Describe tu consulta o solicitud..."
                    disabled={isSubmitting}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center justify-center ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-red-800 hover:bg-red-900'
                  } text-white`}
                >
                  <Send className="h-5 w-5 mr-2" />
                  {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                </button>
              </form>
            </div>

            {/* FAQ */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Preguntas Frecuentes
              </h2>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <span className="font-medium text-gray-800">{faq.question}</span>
                      <span className="text-gray-400">
                        {expandedFaq === index ? '−' : '+'}
                      </span>
                    </button>
                    {expandedFaq === index && (
                      <div className="px-4 pb-3">
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h3 className="font-semibold text-amber-800 mb-2">
                  ¿No encuentras lo que buscas?
                </h3>
                <p className="text-amber-700 text-sm">
                  Nuestro equipo de soporte está disponible para resolver cualquier duda específica
                  sobre el proceso de certificación.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media and Support 
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Síguenos en Redes Sociales
            </h2>
            <p className="text-lg text-gray-600">
              Mantente conectado con nuestra comunidad académica global
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a
              href={contactInfo.socialMedia.linkedin}
              className="flex items-center justify-center p-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <ExternalLink className="h-6 w-6 mr-3" />
              <span className="font-semibold">LinkedIn</span>
            </a>

            <a
              href={contactInfo.socialMedia.twitter}
              className="flex items-center justify-center p-6 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors duration-200"
            >
              <ExternalLink className="h-6 w-6 mr-3" />
              <span className="font-semibold">Twitter</span>
            </a>

            <a
              href={contactInfo.socialMedia.youtube}
              className="flex items-center justify-center p-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
            >
              <ExternalLink className="h-6 w-6 mr-3" />
              <span className="font-semibold">YouTube</span>
            </a>

            <a
              href={contactInfo.socialMedia.researchgate}
              className="flex items-center justify-center p-6 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
            >
              <ExternalLink className="h-6 w-6 mr-3" />
              <span className="font-semibold">ResearchGate</span>
            </a>
          </div>
        </div>
      </section>*/}

      {/* Emergency Support */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-2xl border border-gray-200 p-10 relative overflow-hidden">
            {/* Subtle decorative element */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-50 to-transparent rounded-full -translate-y-32 translate-x-32 opacity-60"></div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
              {/* Content */}
              <div>
                <h2 className="text-3xl font-semibold text-gray-900 mb-4 leading-tight">
                  Alianzas Institucionales
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  ¿Representas una institución educativa? Descubre nuestros programas 
                  especializados para alianzas estratégicas y certificaciones grupales.
                </p>
                
                {/* Services list - more professional */}
                <div className="space-y-4 mb-8">
                  <div className="flex items-center text-gray-700">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-4 flex-shrink-0"></div>
                    <span className="text-base font-medium">Atención prioritaria institucional</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-4 flex-shrink-0"></div>
                    <span className="text-base font-medium">Programas de alianza personalizados</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-4 flex-shrink-0"></div>
                    <span className="text-base font-medium">Certificaciones grupales especializadas</span>
                  </div>
                </div>

                {/* CTA Button */}
                <a
                  href={
                    /Mobi|Android/i.test(navigator.userAgent)
                      ? "https://wa.me/+523173851203?text=Solicito informacion sobre alianzas institucionales"
                      : "https://web.whatsapp.com/send?phone=+523173851203&text=Solicito informacion sobre alianzas institucionales"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-red-800 text-white font-medium rounded-lg hover:bg-red-900 transition-colors duration-200 shadow-sm hover:shadow-md"
                >
                  Contactar Equipo Comercial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>

              {/* Partnership Benefits */}
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Beneficios Institucionales
                  </h3>
                  <p className="text-sm text-gray-600">
                    Programa de alianzas estratégicas
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Descuentos grupales</span>
                    <span className="text-sm font-semibold text-orange-600">Hasta 40%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Soporte dedicado</span>
                    <span className="text-sm font-semibold text-orange-600">24/7 disponible</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Capacitación incluida</span>
                    <span className="text-sm font-semibold text-green-600">Personal especializado</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Reportes detallados</span>
                    <span className="text-sm font-semibold text-orange-600">Dashboard ejecutivo</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;